import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Button, Badge, Textarea, useToast, Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Tabs, TabsContent, TabsList, TabsTrigger
} from '@hiring-intelligence/ui';
import { outreachApi } from '@/services/api';
import { Send, Copy, ExternalLink, Linkedin, Mail } from 'lucide-react';

type Channel = 'whatsapp' | 'linkedin' | 'email';

interface OutreachPanelProps {
	sourcedCandidateId: string;
	hiringRequestId: string;
	candidatePhones?: { value: string; subType?: string }[];
	candidateEmails?: { value: string; subType?: string }[];
	onStatusChange: () => void;
}

const OFFER_STATUSES = [
	{ value: 'pending', label: 'Pending', variant: 'secondary' as const },
	{ value: 'accepted', label: 'Accepted', variant: 'success' as const },
	{ value: 'rejected', label: 'Rejected', variant: 'destructive' as const },
	{ value: 'contact_invalid', label: 'Contact Invalid', variant: 'outline' as const },
	{ value: 'no_response', label: 'No Response', variant: 'outline' as const },
];

function buildWaHref(rawPhone: string, message: string): string {
	const clean = rawPhone.replace(/[\s\-()]/g, '').replace(/^\+/, '');
	return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
}

function buildMailtoHref(email: string, subject: string, body: string): string {
	return `mailto:${email.trim()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function OutreachPanel({ sourcedCandidateId, hiringRequestId, candidatePhones, candidateEmails, onStatusChange }: OutreachPanelProps) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [channel, setChannel] = useState<Channel>('whatsapp');
	const [language, setLanguage] = useState<'id' | 'en'>('id');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [notes, setNotes] = useState('');
	// The editable message draft. `null` means no preview has been generated yet.
	const [messageDraft, setMessageDraft] = useState<string | null>(null);
	// Email-only: the editable subject line.
	const [emailSubject, setEmailSubject] = useState('');

	// Auto-populate phone/email from SignalHire contacts.
	useEffect(() => {
		if (candidatePhones?.length && !phone) {
			setPhone(candidatePhones[0].value);
		}
	}, [candidatePhones]);

	useEffect(() => {
		if (candidateEmails?.length && !email) {
			setEmail(candidateEmails[0].value);
		}
	}, [candidateEmails]);

	// Fetch existing outreach records
	const { data: outreachData } = useQuery({
		queryKey: ['outreach', sourcedCandidateId],
		queryFn: () => outreachApi.listByCandidate(sourcedCandidateId)
	});

	// Create outreach
	const createMutation = useMutation({
		mutationFn: () => outreachApi.create({
			sourcedCandidateId,
			hiringRequestId,
			contactedVia: channel,
			messageLanguage: language,
			phone: channel === 'whatsapp' ? phone : undefined,
			notes,
			message: messageDraft ?? undefined
		}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['outreach', sourcedCandidateId] });
			onStatusChange();
			toast({ title: 'Outreach Created', description: 'Contact record saved.' });
			setNotes('');
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Update outreach status
	const updateMutation = useMutation({
		mutationFn: ({ id, offerStatus }: { id: string; offerStatus: string }) =>
			outreachApi.update(id, { offerStatus }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['outreach', sourcedCandidateId] });
			onStatusChange();
			toast({ title: 'Status Updated' });
		}
	});

	// --- Message preview generation (auto-triggered; no manual button) ---

	const waPreviewMutation = useMutation({
		mutationFn: () => outreachApi.generateWAPreview({
			sourcedCandidateId,
			hiringRequestId,
			language,
			phone: phone || undefined,
		}),
		onSuccess: (res) => setMessageDraft(res.data.message),
		onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' })
	});

	const linkedInPreviewMutation = useMutation({
		mutationFn: () => outreachApi.generateLinkedInPreview({
			sourcedCandidateId,
			hiringRequestId,
			language,
		}),
		onSuccess: (res) => setMessageDraft(res.data.message),
		onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' })
	});

	const emailPreviewMutation = useMutation({
		mutationFn: () => outreachApi.generateEmailPreview({
			sourcedCandidateId,
			hiringRequestId,
			language,
		}),
		onSuccess: (res) => {
			setMessageDraft(res.data.body);
			setEmailSubject(res.data.subject);
		},
		onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' })
	});

	// Auto-generate the preview on open and whenever the channel or language changes.
	// The message text depends only on channel/language (not on the phone/email value),
	// so the consultant sees a ready-to-send default immediately — no Generate button.
	useEffect(() => {
		setMessageDraft(null);
		setEmailSubject('');
		if (channel === 'whatsapp') {
			waPreviewMutation.mutate();
		} else if (channel === 'linkedin') {
			linkedInPreviewMutation.mutate();
		} else {
			emailPreviewMutation.mutate();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [channel, language, sourcedCandidateId, hiringRequestId]);

	const isPreviewing = waPreviewMutation.isPending || linkedInPreviewMutation.isPending || emailPreviewMutation.isPending;
	const outreaches = outreachData?.data || [];
	const hasExisting = outreaches.length > 0;

	const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : channel === 'linkedin' ? 'LinkedIn' : 'Email';
	const previewBoxClass =
		channel === 'whatsapp' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
			: channel === 'linkedin' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
				: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800';
	const previewLabelClass =
		channel === 'whatsapp' ? 'text-green-700 dark:text-green-400'
			: channel === 'linkedin' ? 'text-blue-700 dark:text-blue-400'
				: 'text-violet-700 dark:text-violet-400';

	return (
		<div className="space-y-4 pt-2">
			{/* Existing outreach records */}
			{hasExisting && (
				<div className="space-y-3">
					<h4 className="text-sm font-semibold">Contact History</h4>
					{outreaches.map((outreach: any) => (
						<div key={outreach.id} className="flex items-center justify-between p-3 border rounded-md bg-muted/30">
							<div className="flex-1">
								<div className="flex items-center gap-2 text-sm">
									<Badge variant="outline" className="text-xs capitalize">{outreach.contactedVia}</Badge>
									<span className="text-muted-foreground">
										{outreach.messageLanguage === 'id' ? '🇮🇩' : '🇬🇧'} {new Date(outreach.createdAt).toLocaleDateString()}
									</span>
								</div>
								{outreach.notes && (
									<p className="text-xs text-muted-foreground mt-1">{outreach.notes}</p>
								)}
							</div>
							<div className="flex items-center gap-2">
								<Select
									value={outreach.offerStatus}
									onValueChange={(value) => updateMutation.mutate({ id: outreach.id, offerStatus: value })}
								>
									<SelectTrigger className="w-[140px] h-8 text-xs">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{OFFER_STATUSES.map((s) => (
											<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
										))}
									</SelectContent>
								</Select>
								{outreach.waLink && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => window.open(outreach.waLink, '_blank', 'noopener,noreferrer')}
									>
										<ExternalLink className="h-3 w-3" />
									</Button>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{/* New outreach form */}
			<div className="space-y-3 border-t pt-3">
				<h4 className="text-sm font-semibold">{hasExisting ? 'New Contact' : 'Contact Candidate'}</h4>

				{/* Channel tabs */}
				<Tabs value={channel} onValueChange={(v) => setChannel(v as Channel)}>
					<TabsList className="w-full">
						<TabsTrigger value="whatsapp" className="flex-1 gap-1">
							<Send className="h-3 w-3" /> WhatsApp
						</TabsTrigger>
						<TabsTrigger value="linkedin" className="flex-1 gap-1">
							<Linkedin className="h-3 w-3" /> LinkedIn
						</TabsTrigger>
						<TabsTrigger value="email" className="flex-1 gap-1">
							<Mail className="h-3 w-3" /> Email
						</TabsTrigger>
					</TabsList>

					{/* Language toggle */}
					<div className="flex items-center gap-2 mt-3">
						<span className="text-sm text-muted-foreground">Language:</span>
						<div className="flex gap-1">
							<Button
								variant={language === 'id' ? 'default' : 'outline'}
								size="sm"
								onClick={() => setLanguage('id')}
							>
								🇮🇩 Bahasa
							</Button>
							<Button
								variant={language === 'en' ? 'default' : 'outline'}
								size="sm"
								onClick={() => setLanguage('en')}
							>
								🇬🇧 English
							</Button>
						</div>
					</div>

					<TabsContent value="whatsapp" className="mt-3 space-y-3">
						{/* Phone input */}
						<div className="space-y-2">
							{candidatePhones && candidatePhones.length > 1 && (
								<div className="flex flex-wrap gap-1">
									{candidatePhones.map((p, i) => (
										<Button
											key={i}
											variant={phone === p.value ? 'default' : 'outline'}
											size="sm"
											className="text-xs h-7"
											onClick={() => setPhone(p.value)}
										>
											{p.value}{p.subType ? ` (${p.subType})` : ''}
										</Button>
									))}
								</div>
							)}
							<input
								type="tel"
								placeholder="Phone number (e.g., +6281234567890)"
								value={phone}
								onChange={(e) => setPhone(e.target.value)}
								className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
						</div>
					</TabsContent>

					<TabsContent value="linkedin" className="mt-3">
						<p className="text-xs text-muted-foreground">
							Send the generated message after your connection request is accepted.
						</p>
					</TabsContent>

					<TabsContent value="email" className="mt-3 space-y-3">
						{/* Email recipient input */}
						<div className="space-y-2">
							{candidateEmails && candidateEmails.length > 1 && (
								<div className="flex flex-wrap gap-1">
									{candidateEmails.map((e, i) => (
										<Button
											key={i}
											variant={email === e.value ? 'default' : 'outline'}
											size="sm"
											className="text-xs h-7"
											onClick={() => setEmail(e.value)}
										>
											{e.value}{e.subType ? ` (${e.subType})` : ''}
										</Button>
									))}
								</div>
							)}
							<input
								type="email"
								placeholder="Email address (e.g., name@company.com)"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
						</div>
					</TabsContent>
				</Tabs>

				{/* Auto-generated message preview */}
				{isPreviewing && messageDraft === null ? (
					<div className="border rounded-md p-6 text-center text-sm text-muted-foreground">
						Generating {channelLabel} message…
					</div>
				) : messageDraft !== null ? (
					<div className={`border rounded-md p-3 space-y-2 ${previewBoxClass}`}>
						<div className="flex items-center justify-between">
							<span className={`text-xs font-medium ${previewLabelClass}`}>
								{channelLabel} Message — fill in the [bracketed] details
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 px-2"
								onClick={() => {
									const text = channel === 'email' && emailSubject
										? `Subject: ${emailSubject}\n\n${messageDraft}`
										: messageDraft;
									navigator.clipboard.writeText(text);
									toast({ title: 'Copied', description: 'Message copied to clipboard.' });
								}}
							>
								<Copy className="h-3 w-3 mr-1" /> Copy
							</Button>
						</div>

						{/* Email subject line */}
						{channel === 'email' && (
							<div className="space-y-1">
								<label className="text-xs font-medium text-muted-foreground">Subject</label>
								<Input
									value={emailSubject}
									onChange={(e) => setEmailSubject(e.target.value)}
									className="bg-background/60"
								/>
							</div>
						)}

						<Textarea
							value={messageDraft}
							onChange={(e) => setMessageDraft(e.target.value)}
							className="min-h-[260px] text-sm bg-background/60"
						/>

						{channel === 'whatsapp' && phone && (
							<Button
								size="sm"
								className="w-full bg-green-600 hover:bg-green-700 text-white"
								onClick={() => window.open(buildWaHref(phone, messageDraft), '_blank', 'noopener,noreferrer')}
							>
								<Send className="h-3 w-3 mr-1" /> Open in WhatsApp
							</Button>
						)}

						{channel === 'email' && email && (
							<Button
								size="sm"
								className="w-full bg-violet-600 hover:bg-violet-700 text-white"
								onClick={() => { window.location.href = buildMailtoHref(email, emailSubject, messageDraft); }}
							>
								<Mail className="h-3 w-3 mr-1" /> Open in Email
							</Button>
						)}
					</div>
				) : null}

				{/* Notes */}
				<Textarea
					placeholder="Add notes about this contact attempt..."
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					className="min-h-[60px]"
				/>

				<Button
					onClick={() => createMutation.mutate()}
					disabled={(channel === 'whatsapp' && !phone) || (channel === 'email' && !email) || createMutation.isPending}
					className="w-full"
				>
					{createMutation.isPending ? 'Saving...' : 'Log Contact'}
				</Button>
			</div>
		</div>
	);
}
